import React, { useEffect, useState } from "react";
import { api, formatApiError, relTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ResetPasswordDialog } from "@/components/PasswordTools";
import { LogIn, KeyRound } from "lucide-react";

export const OrgUsersDialog = ({ org, onClose }) => {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState(null);
  const [reset, setReset] = useState(null);
  useEffect(() => { api.get("/super-admin/users", { params: { org_id: org.id } }).then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [org.id]);
  const loginAs = async (u) => {
    try {
      const { data } = await api.post(`/super-admin/impersonate/${u.id}`);
      localStorage.removeItem("tasko.activeProject");
      setUser(data); onClose(); nav("/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sa-users-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-forest">{org.name} · users</DialogTitle>
          <DialogDescription>Log in as anyone in one click. Only you can see these logins.</DialogDescription>
        </DialogHeader>
        {users === null && <div className="text-sm text-forest/50">Loading…</div>}
        <div className="divide-y divide-[#E6E4DD]">
          {users?.map((u) => (
            <div key={u.id} data-testid={`sa-user-${u.id}`} className="py-2.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-forest truncate">{u.name} <span className="text-[10px] uppercase tracking-[0.12em] text-forest/50 ml-1">{u.role}</span>{u.active === false && <span className="text-[10px] text-clay ml-1">disabled</span>}</div>
                <div className="text-xs text-forest/55 truncate">{u.email}</div>
              </div>
              <button data-testid={`sa-reset-${u.id}`} onClick={() => setReset(u)} className="h-8 px-2.5 rounded-sm border border-[#E6E4DD] text-xs text-forest inline-flex items-center gap-1.5 hover:border-forest"><KeyRound className="h-3.5 w-3.5" /> Reset password</button>
              <button data-testid={`sa-login-as-${u.id}`} disabled={u.active === false} onClick={() => loginAs(u)} className="h-8 px-3 rounded-sm bg-forest text-white text-xs font-medium inline-flex items-center gap-1.5 hover:bg-forest-soft disabled:opacity-40"><LogIn className="h-3.5 w-3.5" /> Login as</button>
            </div>
          ))}
          {users?.length === 0 && <div className="text-sm text-forest/50 py-4">No users in this organisation.</div>}
        </div>
        <ResetPasswordDialog target={reset} endpoint={reset ? `/super-admin/users/${reset.id}/reset-password` : ""} onClose={() => setReset(null)} />
      </DialogContent>
    </Dialog>
  );
};

export const AccessLog = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/super-admin/access-logs").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="border border-[#E6E4DD] bg-white rounded-sm p-6" data-testid="sa-access-log">
      <div className="label-caps mb-1">Private access log</div>
      <div className="text-xs text-forest/55 mb-3">Only Super Admins can see this. Organisations never see these logins or resets.</div>
      {rows.length === 0 && <div className="text-sm text-forest/50 py-2">No logins yet.</div>}
      <div className="divide-y divide-[#E6E4DD]">
        {rows.map((r) => (
          <div key={r.id} className="py-2 flex justify-between gap-4 text-sm">
            <span className="text-forest">{r.kind === "login_as" ? `Logged in as ${r.target_name} (${r.target_email})` : `Reset password for ${r.target_name || r.target_user_id.slice(0, 8)}${r.target_email ? ` (${r.target_email})` : ""}`}</span>
            <span className="text-[11px] text-forest/50 shrink-0">{r.super_admin_name || "Super Admin"} · {relTime(r.at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
